/**
 * Main Editor Canvas component
 * Renders the framed artwork preview
 */

import * as React from 'react';
import { cn } from '@frameit/ui';
import { useProjectStore, useSelectedArtwork, getLocalImageUrl } from '../../stores/projectStore';
import { getScenePreset } from '../../data/scenes';
import {
  buildCssFilter,
  buildSceneLightingFilter,
  buildLightDirectionOverlay,
} from './utils/color-filters';
import { computeCSS3DMatrix, computeArtworkCorners } from './utils/perspective';
import { computeEffectCSS } from './effects';
import { getFrameColor } from '../../data/frames';
import { getMatColor } from '../../data/mats';
import { PAPER_PRESETS } from '../../data/papers';
import { GALLERY_BACKGROUNDS } from '../../data/backgrounds';
import { getScenePlacementZone, DEFAULT_PLACEMENT_ZONE } from '../../data/scenes';
import { renderFrame } from './renderers/frame-renderer';
import { useProgressiveImage } from '../../hooks/useProgressiveImage';
import { ResizeHandles } from './ResizeHandles';
import type { FramedArtwork } from '@frameit/types';

/** Calculate gallery layout positions */
function calculateGalleryPositions(
  artworks: FramedArtwork[],
  layoutType: string,
  spacing: number,
  containerWidth: number,
  containerHeight: number,
): Array<{ artwork: FramedArtwork; x: number; y: number; scale: number }> {
  const count = artworks.length;
  if (count === 0) return [];

  const baseSize = Math.min(containerWidth, containerHeight) * 0.15;
  const spacingPx = spacing * 0.5; // Convert mm to rough px

  switch (layoutType) {
    case 'horizontal': {
      const totalWidth = count * baseSize + (count - 1) * spacingPx;
      const scale = totalWidth > containerWidth * 0.9 ? (containerWidth * 0.9) / totalWidth : 1;
      const startX = (containerWidth - totalWidth * scale) / 2;
      return artworks.map((artwork, i) => ({
        artwork,
        x: startX + i * (baseSize + spacingPx) * scale + (baseSize * scale) / 2,
        y: containerHeight / 2,
        scale: scale * 0.8,
      }));
    }

    case 'vertical': {
      const totalHeight = count * baseSize + (count - 1) * spacingPx;
      const scale = totalHeight > containerHeight * 0.9 ? (containerHeight * 0.9) / totalHeight : 1;
      const startY = (containerHeight - totalHeight * scale) / 2;
      return artworks.map((artwork, i) => ({
        artwork,
        x: containerWidth / 2,
        y: startY + i * (baseSize + spacingPx) * scale + (baseSize * scale) / 2,
        scale: scale * 0.8,
      }));
    }

    case 'grid': {
      const cols = Math.ceil(Math.sqrt(count));
      const rows = Math.ceil(count / cols);
      const cellWidth = (containerWidth * 0.8 - spacingPx * (cols - 1)) / cols;
      const cellHeight = (containerHeight * 0.8 - spacingPx * (rows - 1)) / rows;
      const cellSize = Math.min(cellWidth, cellHeight);
      const gridWidth = cols * cellSize + (cols - 1) * spacingPx;
      const gridHeight = rows * cellSize + (rows - 1) * spacingPx;
      const startX = (containerWidth - gridWidth) / 2;
      const startY = (containerHeight - gridHeight) / 2;

      return artworks.map((artwork, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        return {
          artwork,
          x: startX + col * (cellSize + spacingPx) + cellSize / 2,
          y: startY + row * (cellSize + spacingPx) + cellSize / 2,
          scale: 0.7,
        };
      });
    }

    case 'salon': {
      // Organic, asymmetrical arrangement
      const positions: Array<{ artwork: FramedArtwork; x: number; y: number; scale: number }> = [];
      const placed: Array<{ x: number; y: number; w: number; h: number }> = [];

      for (const artwork of artworks) {
        const size = baseSize * (0.7 + Math.random() * 0.6);
        let bestPos = { x: containerWidth / 2, y: containerHeight / 2 };

        // Try to find non-overlapping position
        for (let attempt = 0; attempt < 50; attempt++) {
          const x = size / 2 + Math.random() * (containerWidth - size);
          const y = size / 2 + Math.random() * (containerHeight - size);
          let valid = true;

          for (const p of placed) {
            if (
              x - size / 2 < p.x + p.w + spacingPx &&
              x + size / 2 > p.x - spacingPx &&
              y - size / 2 < p.y + p.h + spacingPx &&
              y + size / 2 > p.y - spacingPx
            ) {
              valid = false;
              break;
            }
          }

          if (valid) {
            bestPos = { x, y };
            break;
          }
        }

        placed.push({ x: bestPos.x - size / 2, y: bestPos.y - size / 2, w: size, h: size });
        positions.push({ artwork, x: bestPos.x, y: bestPos.y, scale: 0.6 + Math.random() * 0.3 });
      }
      return positions;
    }

    case 'symmetrical': {
      const halfCount = Math.ceil(count / 2);
      const verticalSpacing = (containerHeight * 0.8) / halfCount;
      const startY = (containerHeight - verticalSpacing * (halfCount - 1)) / 2;

      return artworks.map((artwork, i) => {
        const row = Math.floor(i / 2);
        const isLeft = i % 2 === 0;
        return {
          artwork,
          x: isLeft ? containerWidth * 0.35 : containerWidth * 0.65,
          y: startY + row * verticalSpacing,
          scale: 0.75,
        };
      });
    }

    case 'staircase': {
      const stepX = (containerWidth * 0.7) / (count - 1 || 1);
      const stepY = (containerHeight * 0.5) / (count - 1 || 1);
      const startX = containerWidth * 0.15;
      const startY = containerHeight * 0.25;

      return artworks.map((artwork, i) => ({
        artwork,
        x: startX + i * stepX,
        y: startY + i * stepY,
        scale: 0.7,
      }));
    }

    case 'circular': {
      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;
      const radius = Math.min(containerWidth, containerHeight) * 0.3;

      return artworks.map((artwork, i) => {
        const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
        return {
          artwork,
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius,
          scale: 0.6,
        };
      });
    }

    default:
      return artworks.map((artwork) => ({
        artwork,
        x: containerWidth / 2,
        y: containerHeight / 2,
        scale: 0.8,
      }));
  }
}

// Paper presets imported from ../../data/papers

/** Generate SVG texture overlay for paper effect */
function generatePaperTextureSvg(texture: string, intensity: number): string {
  const opacity = intensity * 0.5;
  let texturePattern = '';

  switch (texture) {
    case 'woven':
      texturePattern = `
        <pattern id="woven" width="4" height="4" patternUnits="userSpaceOnUse">
          <path d="M0 2h4M2 0v4" stroke="rgba(0,0,0,${opacity})" stroke-width="0.5"/>
        </pattern>
        <rect fill="url(#woven)" width="100%" height="100%"/>
      `;
      break;
    case 'rough':
      texturePattern = `
        <filter id="rough">
          <feTurbulence baseFrequency="0.04" numOctaves="3" result="noise"/>
          <feDiffuseLighting in="noise" lighting-color="white" surfaceScale="${1 + intensity}">
            <feDistantLight azimuth="45" elevation="60"/>
          </feDiffuseLighting>
        </filter>
        <rect fill="white" filter="url(#rough)" width="100%" height="100%" opacity="${opacity * 0.6}"/>
      `;
      break;
    case 'fiber':
      texturePattern = `
        <filter id="fiber">
          <feTurbulence baseFrequency="0.02 0.1" numOctaves="2" result="noise"/>
          <feColorMatrix type="saturate" values="0"/>
        </filter>
        <rect fill="rgba(139,119,101,${opacity * 0.3})" filter="url(#fiber)" width="100%" height="100%"/>
      `;
      break;
    case 'aged':
      texturePattern = `
        <filter id="aged">
          <feTurbulence baseFrequency="0.05" numOctaves="2" result="noise"/>
          <feColorMatrix type="matrix" values="1 0 0 0 0.05  0 1 0 0 0.02  0 0 1 0 -0.05  0 0 0 1 0"/>
        </filter>
        <rect fill="rgba(139,90,43,${opacity * 0.4})" filter="url(#aged)" width="100%" height="100%"/>
      `;
      break;
    case 'subtle':
      texturePattern = `
        <filter id="subtle">
          <feTurbulence baseFrequency="0.8" numOctaves="4" result="noise"/>
          <feColorMatrix type="saturate" values="0"/>
        </filter>
        <rect fill="rgba(0,0,0,${opacity * 0.1})" filter="url(#subtle)" width="100%" height="100%"/>
      `;
      break;
    case 'irregular':
      texturePattern = `
        <filter id="irreg">
          <feTurbulence baseFrequency="0.03" numOctaves="3" result="noise"/>
        </filter>
        <rect fill="rgba(101,67,33,${opacity * 0.25})" filter="url(#irreg)" width="100%" height="100%"/>
      `;
      break;
    default:
      return '';
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" preserveAspectRatio="none">
    ${texturePattern}
  </svg>`;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

export interface EditorCanvasProps {
  /** Custom class name */
  className?: string;
  /** On canvas ready callback */
  onReady?: () => void;
}

/** Editor canvas component */
export function EditorCanvas({ className, onReady }: EditorCanvasProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);
  const [containerSize, setContainerSize] = React.useState({ width: 800, height: 600 });
  const [galleryImages, setGalleryImages] = React.useState<Map<string, string>>(new Map());

  // Drag state for moving artwork in scene
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStartRef = React.useRef<{
    x: number;
    y: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  // Resize handles active state
  const [resizeActive, setResizeActive] = React.useState(false);

  const selectedArtwork = useSelectedArtwork();
  const previewQuality = useProjectStore((s) => s.previewQuality);
  const project = useProjectStore((s) => s.project);
  const sceneZoom = useProjectStore((s) => s.sceneZoom);
  const updateScenePlacement = useProjectStore((s) => s.updateScenePlacement);
  const updateDisplayScaleXY = useProjectStore((s) => s.updateDisplayScaleXY);
  const setScene = useProjectStore((s) => s.setScene);

  // Gallery wall mode
  const galleryWall = project?.galleryWall;
  const framedArtworks = React.useMemo(
    () => project?.framedArtworks || [],
    [project?.framedArtworks],
  );

  // Scene mode
  const scene = project?.scene;
  const scenePreset = scene ? getScenePreset(scene.templateId) : null;
  const scenePlacement = scene?.placements.find((p) => p.framedArtworkId === selectedArtwork?.id);

  // Progressive scene background: thumbnail first, full-res in background
  const sceneBackground = scenePreset?.background;
  const sceneFullUrl = React.useMemo(() => {
    if (!sceneBackground) return undefined;
    const match = sceneBackground.match(/url\((.+)\)/);
    return match ? match[1] : undefined;
  }, [sceneBackground]);
  const sceneBg = useProgressiveImage(scenePreset?.thumbnail, sceneFullUrl);

  // Track container size for gallery layout
  React.useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Load all gallery images
  React.useEffect(() => {
    if (!galleryWall || framedArtworks.length === 0) {
      setGalleryImages(new Map());
      return;
    }

    const newImages = new Map<string, string>();
    for (const artwork of framedArtworks) {
      const url = getLocalImageUrl(artwork.artworkId);
      if (url) {
        newImages.set(artwork.id, url);
      }
    }
    setGalleryImages(newImages);
  }, [galleryWall, framedArtworks]);

  // Load image when artwork changes
  React.useEffect(() => {
    if (!selectedArtwork) {
      setImageUrl(null);
      setImageDimensions(null);
      return;
    }

    let cancelled = false;
    const url = getLocalImageUrl(selectedArtwork.artworkId);
    if (url) {
      setImageUrl(url);
      // Load image to get dimensions
      const img = new Image();
      img.onload = () => {
        if (!cancelled) {
          // Use naturalWidth/naturalHeight for EXIF-corrected dimensions
          setImageDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
          onReady?.();
        }
      };
      img.onerror = () => {
        if (!cancelled) {
          // Blob URL expired or image failed — clear to show loading state
          setImageUrl(null);
          setImageDimensions(null);
        }
      };
      img.src = url;

      return () => {
        cancelled = true;
        img.src = '';
      };
    } else {
      // No URL available (e.g., after page reload with expired blob URLs)
      setImageUrl(null);
      setImageDimensions(null);
    }
  }, [selectedArtwork, onReady]);

  // Calculate frame style
  const frameWidth = selectedArtwork?.frame.width ?? 25;
  const frameColor = selectedArtwork?.frame.templateId
    ? getFrameColor(selectedArtwork.frame.templateId)
    : '#1a1a1a';

  // Calculate physical dimensions (artwork at 300 DPI + mat + frame)
  const physicalDimensions = React.useMemo(() => {
    if (!selectedArtwork || !imageDimensions) return null;

    const DPI = 300;
    const MM_PER_INCH = 25.4;
    const pxToMm = MM_PER_INCH / DPI;

    // Artwork size in mm (assuming 300 DPI print)
    const artworkWidthMm = imageDimensions.width * pxToMm;
    const artworkHeightMm = imageDimensions.height * pxToMm;

    // Mat width in mm (ratio of artwork shortest side)
    const matRatio = selectedArtwork.mat?.width ?? 0;
    const shortestSide = Math.min(artworkWidthMm, artworkHeightMm);
    const matWidthMm = matRatio * shortestSide;

    // Frame width is already in mm
    const frameWidthMm = selectedArtwork.frame.width ?? 25;

    // Total framed size = artwork + 2× mat + 2× frame
    const totalWidthMm = artworkWidthMm + matWidthMm * 2 + frameWidthMm * 2;
    const totalHeightMm = artworkHeightMm + matWidthMm * 2 + frameWidthMm * 2;

    const totalWidthCm = totalWidthMm / 10;
    const totalHeightCm = totalHeightMm / 10;
    const totalWidthIn = totalWidthMm / MM_PER_INCH;
    const totalHeightIn = totalHeightMm / MM_PER_INCH;

    return {
      widthCm: Math.round(totalWidthCm * 10) / 10,
      heightCm: Math.round(totalHeightCm * 10) / 10,
      widthIn: Math.round(totalWidthIn * 10) / 10,
      heightIn: Math.round(totalHeightIn * 10) / 10,
    };
  }, [selectedArtwork, imageDimensions]);

  // Calculate shadow style
  const shadowEnabled = selectedArtwork?.shadow?.enabled ?? true;
  const shadowBlur = selectedArtwork?.shadow?.blur ?? 15;
  const shadowOffsetX = selectedArtwork?.shadow?.offsetX ?? 5;
  const shadowOffsetY = selectedArtwork?.shadow?.offsetY ?? 5;
  const shadowOpacity = selectedArtwork?.shadow?.opacity ?? 0.3;
  const shadowColor = selectedArtwork?.shadow?.color ?? '#000000';

  // Calculate scene placement transform
  const sceneScale = scenePlacement?.scale ?? 0.4;
  const sceneOffsetX = scenePlacement?.offset.x ?? 0;
  const sceneOffsetY = scenePlacement?.offset.y ?? 0;

  // Compute perspective transform for scene placement
  const scenePerspectiveTransform = React.useMemo(() => {
    if (!scenePreset || !selectedArtwork || !imageDimensions) return null;

    const zone = getScenePlacementZone(scenePreset.id) ?? DEFAULT_PLACEMENT_ZONE;

    // Element size — the framed artwork div dimensions (displayScale applied via CSS transform)
    const baseSize = 300;
    const scale = sceneScale;
    const maxDim = baseSize * scale;
    const frameW = selectedArtwork.frame.width ?? 25;
    const matW = selectedArtwork.mat ? (selectedArtwork.mat.width ?? 0.1) * 200 * scale : 0;
    const aspect = imageDimensions.width / imageDimensions.height;
    let imgW: number;
    let imgH: number;
    if (imageDimensions.width >= imageDimensions.height) {
      imgW = Math.min(maxDim, imageDimensions.width * scale);
      imgH = imgW / aspect;
    } else {
      imgH = Math.min(maxDim, imageDimensions.height * scale);
      imgW = imgH * aspect;
    }
    const elementWidth = imgW + (frameW * scale + matW) * 2;
    const elementHeight = imgH + (frameW * scale + matW) * 2;

    // Use ELEMENT aspect ratio (includes frame+mat) for placement, not just image
    const elementAspect = elementWidth / elementHeight;
    const artworkCorners = computeArtworkCorners(zone.corners, elementAspect, sceneScale, {
      x: sceneOffsetX,
      y: sceneOffsetY,
    });

    const matrix = computeCSS3DMatrix(
      artworkCorners,
      containerSize.width,
      containerSize.height,
      elementWidth,
      elementHeight,
    );

    return matrix || null;
  }, [
    scenePreset,
    selectedArtwork,
    imageDimensions,
    sceneScale,
    sceneOffsetX,
    sceneOffsetY,
    containerSize,
  ]);

  // Scene lighting filter — subtle tint to match room ambiance
  const sceneLightingFilter = React.useMemo(() => {
    if (!scenePreset) return 'none';
    return buildSceneLightingFilter(scenePreset.colorTemperature, scenePreset.lightSource);
  }, [scenePreset]);

  const sceneLightOverlay = React.useMemo(() => {
    if (!scenePreset?.lightSource) return '';
    return buildLightDirectionOverlay(scenePreset.lightSource);
  }, [scenePreset]);

  // Ensure placement exists for dragging
  const projectScene = project?.scene;
  const ensurePlacementExists = React.useCallback(() => {
    if (!selectedArtwork || !projectScene) return;
    if (scenePlacement) return; // Already exists

    // Add placement for this artwork
    setScene({
      ...projectScene,
      placements: [
        ...projectScene.placements,
        {
          framedArtworkId: selectedArtwork.id,
          zoneId: 'main',
          scale: 0.4,
          offset: { x: 0, y: 0 },
        },
      ],
    });
  }, [selectedArtwork, projectScene, scenePlacement, setScene]);

  // Handle drag start (mouse or touch)
  const handleDragStart = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!scenePreset || !selectedArtwork) return;
      ensurePlacementExists();
      setIsDragging(true);
      dragStartRef.current = {
        x: clientX,
        y: clientY,
        offsetX: sceneOffsetX,
        offsetY: sceneOffsetY,
      };
    },
    [scenePreset, selectedArtwork, sceneOffsetX, sceneOffsetY, ensurePlacementExists],
  );

  // Handle drag move
  const handleDragMove = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging || !dragStartRef.current || !selectedArtwork || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const deltaX = (clientX - dragStartRef.current.x) / containerRect.width;
      const deltaY = (clientY - dragStartRef.current.y) / containerRect.height;

      // Scale the delta to match the offset units (roughly -1.5 to 1.5 range)
      const newOffsetX = Math.max(-1.5, Math.min(1.5, dragStartRef.current.offsetX + deltaX * 2));
      const newOffsetY = Math.max(-1.0, Math.min(1.0, dragStartRef.current.offsetY + deltaY * 2));

      updateScenePlacement(selectedArtwork.id, {
        offset: { x: newOffsetX, y: newOffsetY },
      });
    },
    [isDragging, selectedArtwork, updateScenePlacement],
  );

  // Handle drag end
  const handleDragEnd = React.useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  // Mouse event handlers
  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleDragMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Touch event handlers
  React.useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    const handleTouchEnd = () => {
      handleDragEnd();
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Render framed artwork element (reused in both modes)
  const renderFramedArtwork = (inScene: boolean) => {
    if (!selectedArtwork || !imageUrl) return null;

    // displayScale is applied as CSS transform on the wrapper, not per-dimension
    const baseSize = inScene
      ? 300
      : Math.min(containerSize.width * 0.6, containerSize.height * 0.6, 400);
    const scale = inScene ? sceneScale : 1;
    const maxDim = baseSize * scale;

    // Image transform (crop/rotate/flip)
    const imgTransform = selectedArtwork.imageTransform;
    const crop = imgTransform?.cropRect;
    const rotation = imgTransform?.rotation ?? 0;
    const flipH = imgTransform?.flipH ?? false;
    const flipV = imgTransform?.flipV ?? false;

    // Effective image dimensions after crop and rotation
    let effectiveWidth = imageDimensions?.width ?? maxDim;
    let effectiveHeight = imageDimensions?.height ?? maxDim;
    if (crop && imageDimensions) {
      effectiveWidth = imageDimensions.width * crop.width;
      effectiveHeight = imageDimensions.height * crop.height;
    }
    // Rotation 90/270 swaps width/height
    if (rotation === 90 || rotation === 270) {
      const tmp = effectiveWidth;
      effectiveWidth = effectiveHeight;
      effectiveHeight = tmp;
    }

    // Calculate frame and mat widths
    const scaledFrameWidth = frameWidth * scale;
    const matWidth = selectedArtwork.mat ? (selectedArtwork.mat.width ?? 0.1) * 200 * scale : 0;
    const totalBorderWidth = scaledFrameWidth + matWidth;

    // Scene transform: use perspective matrix3d when available, fallback to translate
    const usePerspective = inScene && scenePerspectiveTransform;
    const translateX = inScene && !usePerspective ? `calc(${sceneOffsetX * 50}vw)` : undefined;
    const translateY = inScene && !usePerspective ? `calc(${sceneOffsetY * 50}vh)` : undefined;

    // Mat color and adjustments
    const matColor = selectedArtwork.mat ? getMatColor(selectedArtwork.mat.templateId) : undefined;
    const matHue = selectedArtwork.mat?.colorAdjustments?.hue ?? 0;
    const matSaturation = selectedArtwork.mat?.colorAdjustments?.saturation ?? 0;
    const matLightness = selectedArtwork.mat?.colorAdjustments?.lightness ?? 0;
    const matFilter = `hue-rotate(${matHue}deg) saturate(${100 + matSaturation}%) brightness(${100 + matLightness}%)`;

    // Color grading filter for the artwork image
    const colorFilter = buildCssFilter(selectedArtwork.colorGrading);

    // Compute visual effects from effects array
    const effectStyles = selectedArtwork.effects
      .filter((e) => e.enabled)
      .map((e) => computeEffectCSS(e.templateId, e.params))
      .filter(Boolean);

    // Merge effect CSS filters with color grading filter
    const effectFilterParts = effectStyles.map((s) => s?.filter).filter(Boolean);
    const combinedFilter =
      [colorFilter !== 'none' ? colorFilter : '', ...effectFilterParts].filter(Boolean).join(' ') ||
      undefined;

    // Render frame using Canvas 2D renderer (use effective dimensions for sizing)
    // Fit image within maxDim while preserving aspect ratio
    let imgW: number;
    let imgH: number;
    if (imageDimensions) {
      const aspect = effectiveWidth / effectiveHeight;
      if (effectiveWidth >= effectiveHeight) {
        imgW = Math.min(maxDim, effectiveWidth * scale);
        imgH = imgW / aspect;
      } else {
        imgH = Math.min(maxDim, effectiveHeight * scale);
        imgW = imgH * aspect;
      }
    } else {
      imgW = maxDim;
      imgH = maxDim;
    }
    const innerW = Math.round(imgW + matWidth * 2);
    const innerH = Math.round(imgH + matWidth * 2);

    let frameBackgroundImage: string | undefined;
    try {
      const frameCanvas = renderFrame({
        templateId: selectedArtwork.frame.templateId,
        frameWidth: Math.round(scaledFrameWidth),
        innerWidth: innerW,
        innerHeight: innerH,
        highlightIntensity: selectedArtwork.frame.bevelAdjustments?.highlightIntensity,
        shadowIntensity: selectedArtwork.frame.bevelAdjustments?.shadowIntensity,
      });
      frameBackgroundImage = frameCanvas.toDataURL();
    } catch {
      // Fallback to solid color if canvas rendering fails
    }

    return (
      <div
        className="relative inline-block"
        style={{
          padding: `${totalBorderWidth}px`,
          backgroundColor: frameColor,
          backgroundImage: frameBackgroundImage ? `url(${frameBackgroundImage})` : undefined,
          backgroundSize: '100% 100%',
          boxShadow: shadowEnabled
            ? `${shadowOffsetX * scale}px ${shadowOffsetY * scale}px ${shadowBlur * scale}px ${shadowColor}${Math.round(
                shadowOpacity * 255,
              )
                .toString(16)
                .padStart(2, '0')}`
            : 'none',
          transform:
            !usePerspective && inScene ? `translate(${translateX}, ${translateY})` : undefined,
        }}
      >
        {/* Mat layer(s) with bevel */}
        {selectedArtwork.mat &&
          matColor &&
          (() => {
            const layers = selectedArtwork.mat.layers ?? 1;
            const bevelColor = selectedArtwork.mat.bevelColor ?? '#e8e0d0';
            const secondColor = selectedArtwork.mat.secondLayerTemplateId
              ? getMatColor(selectedArtwork.mat.secondLayerTemplateId)
              : matColor;
            const bevelWidth = Math.max(1, matWidth * 0.04 * scale);

            if (layers === 1) {
              // Single mat with inner bevel shadow
              return (
                <div
                  className="absolute"
                  style={{
                    inset: `${scaledFrameWidth}px`,
                    backgroundColor: matColor,
                    filter: matFilter,
                    boxShadow: `inset 2px 2px 4px rgba(255,255,255,0.15), inset -1px -1px 3px rgba(0,0,0,0.1)`,
                  }}
                />
              );
            }

            // Multi-layer mat: outer mat → bevel line → inner mat → bevel line → (optional third) → artwork
            const outerInset = scaledFrameWidth;
            const layerWidth = matWidth / layers;
            return (
              <>
                {/* Outer mat layer */}
                <div
                  className="absolute"
                  style={{
                    inset: `${outerInset}px`,
                    backgroundColor: matColor,
                    filter: matFilter,
                    boxShadow: `inset 2px 2px 3px rgba(255,255,255,0.12), inset -1px -1px 2px rgba(0,0,0,0.08)`,
                  }}
                />
                {/* Bevel line between outer and inner */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    inset: `${outerInset + layerWidth - bevelWidth}px`,
                    border: `${bevelWidth}px solid ${bevelColor}`,
                    boxShadow: `inset 1px 1px 1px rgba(255,255,255,0.3), -1px -1px 1px rgba(0,0,0,0.15)`,
                  }}
                />
                {/* Inner mat layer */}
                <div
                  className="absolute"
                  style={{
                    inset: `${outerInset + layerWidth}px`,
                    backgroundColor: secondColor,
                    filter: matFilter,
                    boxShadow: `inset 1px 1px 2px rgba(255,255,255,0.1), inset -1px -1px 2px rgba(0,0,0,0.08)`,
                  }}
                />
                {/* Third layer bevel + mat if triple */}
                {layers >= 3 && (
                  <>
                    <div
                      className="absolute pointer-events-none"
                      style={{
                        inset: `${outerInset + layerWidth * 2 - bevelWidth}px`,
                        border: `${bevelWidth}px solid ${bevelColor}`,
                        boxShadow: `inset 1px 1px 1px rgba(255,255,255,0.25), -1px -1px 1px rgba(0,0,0,0.1)`,
                      }}
                    />
                    <div
                      className="absolute"
                      style={{
                        inset: `${outerInset + layerWidth * 2}px`,
                        backgroundColor: matColor,
                        filter: matFilter,
                      }}
                    />
                  </>
                )}
              </>
            );
          })()}

        {/* Artwork image with paper effect + image transforms */}
        <div className="relative">
          {/* Crop wrapper — clips to cropped region */}
          <div
            className="overflow-hidden"
            style={{
              width: imgW,
              height: imgH,
            }}
          >
            <img
              src={imageUrl}
              alt="Artwork"
              className="block"
              style={{
                // When cropped: scale image up and offset to show only the crop region
                ...(crop && imageDimensions
                  ? {
                      width: imgW / crop.width,
                      height: imgH / crop.height,
                      marginLeft: -(crop.x / crop.width) * imgW,
                      marginTop: -(crop.y / crop.height) * imgH,
                      maxWidth: 'none',
                      maxHeight: 'none',
                    }
                  : {
                      width: imgW,
                      height: imgH,
                      objectFit: 'contain' as const,
                    }),
                // Rotation + flip transform
                ...((rotation !== 0 || flipH || flipV) && {
                  transform: [
                    rotation !== 0 ? `rotate(${rotation}deg)` : '',
                    flipH ? 'scaleX(-1)' : '',
                    flipV ? 'scaleY(-1)' : '',
                  ]
                    .filter(Boolean)
                    .join(' '),
                  transformOrigin: 'center center',
                }),
                filter: combinedFilter,
              }}
            />
          </div>

          {/* Paper texture overlay */}
          {selectedArtwork.paper &&
            (() => {
              const preset = PAPER_PRESETS[selectedArtwork.paper.templateId];
              if (!preset || preset.texture === 'none') return null;

              const textureIntensity = selectedArtwork.paper.textureIntensity ?? 0.5;
              const displacementIntensity = selectedArtwork.paper.displacementIntensity ?? 0.3;
              const textureSvg = generatePaperTextureSvg(preset.texture, textureIntensity);

              return (
                <>
                  {/* Paper base color tint */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundColor: preset.color,
                      mixBlendMode: 'multiply',
                      opacity: textureIntensity * 0.3,
                    }}
                  />

                  {/* Paper texture pattern */}
                  {textureSvg && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage: `url("${textureSvg}")`,
                        backgroundSize: 'cover',
                        mixBlendMode: 'overlay',
                        opacity: textureIntensity,
                      }}
                    />
                  )}

                  {/* 3D depth effect using inner shadow */}
                  {displacementIntensity > 0 && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        boxShadow: `inset 0 0 ${20 * displacementIntensity}px rgba(0,0,0,${0.1 * displacementIntensity}), inset ${2 * displacementIntensity}px ${2 * displacementIntensity}px ${8 * displacementIntensity}px rgba(255,255,255,${0.15 * displacementIntensity})`,
                      }}
                    />
                  )}
                </>
              );
            })()}

          {/* Visual effect overlays (gradients, textures) */}
          {effectStyles.map((style, idx) => {
            if (!style?.overlay) return null;
            return (
              <div
                key={idx}
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: style.overlay,
                  backgroundSize: 'cover',
                  mixBlendMode: (style.overlayBlendMode ??
                    'normal') as React.CSSProperties['mixBlendMode'],
                  opacity: style.overlayOpacity ?? 1,
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  // Render a single framed artwork for gallery wall
  const renderGalleryArtwork = (artwork: FramedArtwork, imageUrl: string, scale: number) => {
    const displayScale = artwork.displayScale ?? 1;
    const finalScale = scale * displayScale;
    const artworkFrameWidth = artwork.frame.width ?? 25;
    const artworkFrameColor = artwork.frame.templateId
      ? getFrameColor(artwork.frame.templateId)
      : '#1a1a1a';
    const artworkShadowEnabled = artwork.shadow?.enabled ?? true;
    const artworkShadowBlur = artwork.shadow?.blur ?? 15;
    const artworkShadowOffsetX = artwork.shadow?.offsetX ?? 5;
    const artworkShadowOffsetY = artwork.shadow?.offsetY ?? 5;
    const artworkShadowOpacity = artwork.shadow?.opacity ?? 0.3;

    const size = 100 * finalScale;

    return (
      <div
        className="relative"
        style={{
          boxShadow: artworkShadowEnabled
            ? `${artworkShadowOffsetX * finalScale}px ${artworkShadowOffsetY * finalScale}px ${artworkShadowBlur * finalScale}px rgba(0,0,0,${artworkShadowOpacity})`
            : 'none',
        }}
      >
        {/* Frame */}
        <div
          className="absolute inset-0"
          style={{
            border: `${artworkFrameWidth * finalScale * 0.3}px solid ${artworkFrameColor}`,
            boxShadow: `inset 1px 1px 2px rgba(255,255,255,0.2), inset -1px -1px 2px rgba(0,0,0,0.3)`,
          }}
        />

        {/* Mat */}
        {artwork.mat && (
          <div
            className="absolute"
            style={{
              inset: `${artworkFrameWidth * finalScale * 0.3}px`,
              border: `${(artwork.mat.width ?? 0.1) * 60 * finalScale}px solid ${getMatColor(artwork.mat.templateId)}`,
            }}
          />
        )}

        {/* Artwork image */}
        <img
          src={imageUrl}
          alt="Artwork"
          className="block"
          style={{
            width: size,
            height: size,
            objectFit: 'contain',
          }}
        />
      </div>
    );
  };

  // Gallery wall background color
  const galleryBackground = galleryWall
    ? GALLERY_BACKGROUNDS[galleryWall.sceneTemplateId] || '#ffffff'
    : undefined;

  // Calculate gallery positions
  const galleryPositions = galleryWall
    ? calculateGalleryPositions(
        framedArtworks,
        galleryWall.presetId,
        galleryWall.spacing || 50,
        containerSize.width,
        containerSize.height,
      )
    : [];

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full h-full min-h-[300px] rounded-lg overflow-hidden flex items-center justify-center',
        !scenePreset && !galleryWall && 'bg-muted/50',
        className,
      )}
      onClick={() => setResizeActive(false)}
      style={
        galleryWall
          ? {
              backgroundColor: galleryBackground,
            }
          : undefined
      }
    >
      {/* Scene background layer (progressive: thumbnail → full-res) */}
      {scenePreset && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: sceneBg.src ? `url(${sceneBg.src})` : scenePreset.background,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: scenePreset.wallColor || '#f0f0f0',
            transform: `scale(${sceneZoom / 100})`,
            transformOrigin: 'center center',
            transition: sceneBg.isLoading ? undefined : 'background-image 0.3s ease',
          }}
        />
      )}

      {/* Gallery wall mode */}
      {galleryWall && galleryPositions.length > 0 && (
        <div className="absolute inset-0">
          {/* Wall texture overlay */}
          <div
            className="absolute inset-0 pointer-events-none opacity-10"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
            }}
          />

          {/* Render all artworks */}
          {galleryPositions.map(({ artwork, x, y, scale }) => {
            const artworkImageUrl = galleryImages.get(artwork.id);
            if (!artworkImageUrl) return null;

            return (
              <div
                key={artwork.id}
                className="absolute"
                style={{
                  left: x,
                  top: y,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {renderGalleryArtwork(artwork, artworkImageUrl, scale)}
              </div>
            );
          })}

          {/* Gallery wall label */}
          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white capitalize">
            {galleryWall.presetId} Layout
          </div>
        </div>
      )}

      {/* Scene mode */}
      {scenePreset && !galleryWall && selectedArtwork && imageUrl && (
        <div
          className={cn(
            'relative w-full h-full',
            !scenePerspectiveTransform && 'flex items-center justify-center',
          )}
        >
          {/* Subtle vignette overlay for better artwork visibility */}
          <div
            className="absolute inset-0 pointer-events-none z-10"
            style={{
              background:
                'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.15) 100%)',
            }}
          />

          {/* Framed artwork in scene - draggable, with lighting match */}
          <div
            className={cn(
              'cursor-grab active:cursor-grabbing select-none relative',
              isDragging && 'cursor-grabbing',
              scenePerspectiveTransform && 'absolute top-0 left-0',
            )}
            style={{
              filter: sceneLightingFilter !== 'none' ? sceneLightingFilter : undefined,
              transform: scenePerspectiveTransform
                ? `${scenePerspectiveTransform} scale(${(selectedArtwork.displayScale ?? 1) * (selectedArtwork.displayScaleX ?? 1)}, ${(selectedArtwork.displayScale ?? 1) * (selectedArtwork.displayScaleY ?? 1)})`
                : `scale(${(selectedArtwork.displayScale ?? 1) * (selectedArtwork.displayScaleX ?? 1)}, ${(selectedArtwork.displayScale ?? 1) * (selectedArtwork.displayScaleY ?? 1)})`,
              transformOrigin: scenePerspectiveTransform ? '0 0' : 'center center',
              transition: isDragging ? undefined : 'transform 0.15s ease-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setResizeActive(true);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              handleDragStart(e.clientX, e.clientY);
            }}
            onTouchStart={(e) => {
              handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
            }}
          >
            {renderFramedArtwork(true)}
            {/* Directional light overlay */}
            {sceneLightOverlay && (
              <div
                className="absolute inset-0 pointer-events-none rounded-sm"
                style={{ backgroundImage: sceneLightOverlay }}
              />
            )}
            <ResizeHandles
              active={resizeActive}
              scale={selectedArtwork.displayScale ?? 1}
              scaleX={selectedArtwork.displayScaleX ?? 1}
              scaleY={selectedArtwork.displayScaleY ?? 1}
              onScaleChange={(sx, sy) => {
                updateDisplayScaleXY(selectedArtwork.id, sx, sy);
              }}
            />
          </div>

          {/* Drag hint + dimensions */}
          {!isDragging && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 pointer-events-none z-20">
              <div className="px-3 py-1.5 bg-black/60 rounded-full text-xs text-white">
                Drag to reposition
              </div>
              {physicalDimensions && (
                <div className="px-2 py-1.5 bg-black/60 rounded-full text-[10px] text-white/80 whitespace-nowrap">
                  {physicalDimensions.widthCm} × {physicalDimensions.heightCm} cm
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Regular mode (no scene, no gallery) */}
      {!scenePreset && !galleryWall && selectedArtwork && imageUrl && (
        <div className="flex flex-col items-center">
          <div
            className="relative"
            style={{
              transform: `scale(${(selectedArtwork.displayScale ?? 1) * (selectedArtwork.displayScaleX ?? 1)}, ${(selectedArtwork.displayScale ?? 1) * (selectedArtwork.displayScaleY ?? 1)})`,
              transformOrigin: 'center center',
              transition: 'transform 0.15s ease-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setResizeActive(true);
            }}
          >
            {renderFramedArtwork(false)}
            <ResizeHandles
              active={resizeActive}
              scale={selectedArtwork.displayScale ?? 1}
              scaleX={selectedArtwork.displayScaleX ?? 1}
              scaleY={selectedArtwork.displayScaleY ?? 1}
              onScaleChange={(sx, sy) => {
                updateDisplayScaleXY(selectedArtwork.id, sx, sy);
              }}
            />
          </div>
          {physicalDimensions && (
            <div className="mt-2 px-2 py-1 bg-black/40 rounded text-[10px] text-white/80 whitespace-nowrap">
              {physicalDimensions.widthCm} × {physicalDimensions.heightCm} cm
              <span className="mx-1.5 text-white/40">|</span>
              {physicalDimensions.widthIn} × {physicalDimensions.heightIn}"
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!selectedArtwork && !galleryWall && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
          <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-sm">Upload an image to get started</p>
        </div>
      )}

      {/* Loading state when artwork selected but image not loaded */}
      {selectedArtwork && !imageUrl && !galleryWall && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm">Loading artwork...</p>
        </div>
      )}

      {/* Scene name indicator */}
      {scenePreset && !galleryWall && (
        <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white">
          {scenePreset.name}
        </div>
      )}

      {/* Quality indicator */}
      <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 rounded text-xs text-white">
        {previewQuality.toUpperCase()}
      </div>
    </div>
  );
}
